/**
 * Life4Earth V2 — KPI Progress Entries API Adapter
 *
 * Replaces: localStorage key l4e_kpi_progress_entries
 * Endpoint: /api/KpiProgressEntries
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── Types ────────────────────────────────────────────────────

export interface KpiProgressEntryResponse {
  id: string;
  entryGuid: string;
  projectId: string;
  kpiKey: string;
  reportingDate: string;
  actualComplete: number | null;
  note: string | null;
  evidenceUrl: string | null;
  status: string;
  workflowGuid: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
}

export interface CreateKpiProgressEntryRequest {
  entryGuid: string;
  projectId: string;
  kpiKey?: string;
  reportingDate: string;
  actualComplete?: number;
  note?: string;
  evidenceUrl?: string;
  status?: string;
  workflowGuid?: string;
}

// ── API Functions ────────────────────────────────────────────

export async function fetchAllKpiProgressEntries(): Promise<KpiProgressEntryResponse[]> {
  return apiFetch<KpiProgressEntryResponse[]>("/api/KpiProgressEntries/all");
}

export async function fetchKpiProgressByProject(
  projectId: string,
  status?: string
): Promise<KpiProgressEntryResponse[]> {
  const params = new URLSearchParams({ projectId });
  if (status) params.set("status", status);
  return apiFetch<KpiProgressEntryResponse[]>(`/api/KpiProgressEntries?${params.toString()}`);
}

export async function upsertKpiProgressEntry(
  request: CreateKpiProgressEntryRequest
): Promise<KpiProgressEntryResponse> {
  return apiFetch<KpiProgressEntryResponse>("/api/KpiProgressEntries", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function batchUpsertKpiProgressEntries(
  entries: CreateKpiProgressEntryRequest[]
): Promise<KpiProgressEntryResponse[]> {
  return apiFetch<KpiProgressEntryResponse[]>("/api/KpiProgressEntries/batch", {
    method: "POST",
    body: JSON.stringify({ entries }),
  });
}

export async function updateKpiProgressEntryStatus(
  entryGuid: string,
  status: string,
  approvedBy?: string
): Promise<KpiProgressEntryResponse> {
  return apiFetch<KpiProgressEntryResponse>(`/api/KpiProgressEntries/${entryGuid}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status, approvedBy }),
  });
}

export async function deleteKpiProgressEntry(entryGuid: string): Promise<void> {
  return apiFetch<void>(`/api/KpiProgressEntries/${entryGuid}`, {
    method: "DELETE",
  });
}

/** Convert API response to the localStorage shape used by progressEntries.ts */
export function kpiProgressEntryToStoreShape(r: KpiProgressEntryResponse): Record<string, any> {
  return {
    entry_guid: normalizeGuid(r.entryGuid),
    project_guid: normalizeGuid(r.projectId),
    kpi_key: r.kpiKey,
    reporting_date: r.reportingDate,
    actual_complete: r.actualComplete ?? 0,
    note: r.note || undefined,
    evidence_url: r.evidenceUrl || undefined,
    status: r.status,
    workflow_guid: normalizeGuid(r.workflowGuid),
    created_by: normalizeGuid(r.createdBy),
    created_at: r.createdAt,
    updated_at: r.updatedAt,
    approved_by: normalizeGuid(r.approvedBy),
    approved_at: r.approvedAt || undefined,
  };
}
